import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Bot, Save, Eye, ArrowLeft, Upload, FileText, 
  Palette, MessageSquare, Settings, Globe, CheckCircle, Trash2, Plus, X, Menu, LogOut, User, ChevronDown
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../translations';
import LanguageToggle from './LanguageToggle';
import { API_URL } from '../utils/api';

const BotEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language, isRTL } = useLanguage();
  const { user, logout } = useAuth();
  const { t } = useTranslation(language);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [chatbot, setChatbot] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [knowledgeBase, setKnowledgeBase] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_type: 'general',
    language: 'en',
    collect_user_info: false,
    custom_greeting: '',
    custom_colors: null,
    custom_logo: '',
    quick_questions: []
  });

  const handleLogout = async () => {
    try {
      await logout();
      toast.success(t('navigation.logout'));
      navigate('/login');
      setIsProfileMenuOpen(false);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchChatbot();
  }, [slug]);

  const fetchChatbot = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/chatbots/${slug}`);
      const bot = response.data;
      
      setChatbot(bot);
      setFormData({
        name: bot.name,
        description: bot.description || '',
        template_type: bot.template_type || 'general',
        language: bot.language || 'en',
        collect_user_info: bot.collect_user_info || false,
        custom_greeting: bot.custom_greeting || '',
        custom_colors: bot.custom_colors || null,
        custom_logo: bot.custom_logo || '',
        quick_questions: bot.quick_questions || []
      });
      setKnowledgeBase(bot.knowledge_base || '');
    } catch (error) {
      console.error('Error fetching chatbot:', error);
      toast.error('Failed to load chatbot');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTemplateSelect = (templateId) => {
    const selectedTemplate = templates.find(t => t.id === templateId);
    setFormData(prev => ({
      ...prev,
      template_type: templateId,
      custom_greeting: prev.custom_greeting || selectedTemplate.greeting
    }));
  };

  const handleQuickQuestionsChange = (questions) => {
    setFormData(prev => ({
      ...prev,
      quick_questions: questions
    }));
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }

      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File ${file.name} is not supported. Please upload PDF, DOCX, or TXT files.`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API_URL}/process-file`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        setUploadedFiles(prev => [...prev, {
          name: file.name,
          size: file.size,
          content: response.data.text
        }]);

        setKnowledgeBase(prev => prev + '\n\n' + response.data.text);
        toast.success(`File ${file.name} processed successfully!`);
      } catch (error) {
        console.error('File upload error:', error);
        toast.error(`Failed to process file ${file.name}`);
      }
    }
  };

  const removeFile = (index) => {
    const removedFile = uploadedFiles[index];
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    
    // Remove the file content from knowledge base
    const newKnowledgeBase = knowledgeBase.replace(removedFile.content, '').replace(/\n\n+/g, '\n\n').trim();
    setKnowledgeBase(newKnowledgeBase);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a chatbot name');
      return;
    }

    if (!knowledgeBase.trim()) {
      toast.error('Please add some knowledge base content');
      return;
    }

    setSaving(true);
    
    try {
      await axios.put(`${API_URL}/chatbots/${slug}`, {
        name: formData.name,
        description: formData.description,
        knowledgeBase: knowledgeBase.trim(),
        templateType: formData.template_type,
        language: formData.language,
        collectUserInfo: formData.collect_user_info,
        customGreeting: formData.custom_greeting,
        customColors: formData.custom_colors,
        customLogo: formData.custom_logo,
        quickQuestions: formData.quick_questions
      });
      
      toast.success('ChatBot updated successfully!');
      fetchChatbot(); // Refresh data
    } catch (error) {
      console.error('Error updating chatbot:', error);
      toast.error(error.response?.data?.message || 'Failed to update chatbot');
    } finally {
      setSaving(false);
    }
  };

  const addQuickQuestion = () => {
    const newQuestion = prompt('Enter a quick question:');
    if (newQuestion && newQuestion.trim()) {
      handleQuickQuestionsChange([...formData.quick_questions, newQuestion.trim()]);
    }
  };

  const removeQuickQuestion = (index) => {
    const newQuestions = formData.quick_questions.filter((_, i) => i !== index);
    handleQuickQuestionsChange(newQuestions);
  };

  const templates = [
    {
      id: 'general',
      name: 'General Purpose',
      description: 'A versatile template suitable for any type of business or service',
      icon: '⚡',
      color: 'from-blue-500 to-blue-600',
      greeting: "Hello! I'm here to help you. How can I assist you today?"
    },
    {
      id: 'hospital',
      name: 'Hospital/Medical',
      description: 'Designed for hospitals and healthcare facilities',
      icon: '🏥',
      color: 'from-red-500 to-red-600',
      greeting: "Welcome to our hospital! I'm here to help you with information about our services, appointments, and more. How can I assist you?"
    },
    {
      id: 'restaurant',
      name: 'Restaurant/Food',
      description: 'Perfect for restaurants, cafes, and food services',
      icon: '🍽️',
      color: 'from-orange-500 to-orange-600',
      greeting: "Welcome to our restaurant! I can help you with our menu, reservations, delivery, and more. What would you like to know?"
    },
    {
      id: 'business',
      name: 'Business/Corporate',
      description: 'Ideal for professional services and corporate businesses',
      icon: '🏢',
      color: 'from-purple-500 to-purple-600',
      greeting: "Hello! Welcome to our business. I'm here to help you with information about our products and services. How can I assist you?"
    },
    {
      id: 'ecommerce',
      name: 'E-commerce/Shop',
      description: 'Great for online stores and retail businesses',
      icon: '🛍️',
      color: 'from-green-500 to-green-600',
      greeting: "Welcome to our store! I can help you find products, track orders, and answer questions about our services. What can I help you with?"
    },
    {
      id: 'education',
      name: 'Education/School',
      description: 'Perfect for educational institutions and training centers',
      icon: '🎓',
      color: 'from-indigo-500 to-indigo-600',
      greeting: "Welcome to our educational institution! I can help you with course information, admissions, and academic support. How can I assist you?"
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Ultra-Glassy Header - Fixed to top */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/50 backdrop-blur-3xl border-b border-white/30 shadow-2xl shadow-gray-300/20" style={{backdropFilter: 'blur(40px) saturate(200%)', WebkitBackdropFilter: 'blur(40px) saturate(200%)'}}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex justify-between items-center h-20">
              
              {/* Logo */}
              <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Link 
                  to="/dashboard" 
                  className={`flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                  <span className="text-sm font-medium">{t('common.back')}</span>
                </Link>
                
                <Link to="/" className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-sm">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-lg font-bold text-gray-900">{t('app.name')}</span>
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className={`hidden md:flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <LanguageToggle className="text-gray-700 hover:text-gray-900" />
                
                {user && (
                  <>
                    <Link 
                      to="/dashboard" 
                      className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
                    >
                      {t('navigation.dashboard')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-red-600 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{t('navigation.logout')}</span>
                    </button>
                  </>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className="md:hidden py-4 border-t border-gray-100">
                <div className="flex flex-col gap-3">
                  <LanguageToggle className="w-full justify-center" />
                  {user && (
                    <>
                      <Link 
                        to="/dashboard" 
                        className="px-4 py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('navigation.dashboard')}
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMobileMenuOpen(false);
                        }}
                        className="px-4 py-2 text-center text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        {t('navigation.logout')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </header>
        
        {/* Spacer for fixed header */}
        <div className="h-20"></div>
        
        <div className="px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 text-lg">Loading chatbot...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header - Fixed to top */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/50 backdrop-blur-3xl border-b border-white/30 shadow-2xl shadow-gray-300/20" style={{backdropFilter: 'blur(40px) saturate(200%)', WebkitBackdropFilter: 'blur(40px) saturate(200%)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex justify-between items-center h-20">
            
            {/* Elegant Logo */}
            <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Link 
                to="/dashboard" 
                className={`flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                <span className="text-sm font-medium">{t('common.back')}</span>
              </Link>
              
              <Link to="/" className={`flex items-center gap-3 group ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="relative">
                  <div className="absolute inset-0 bg-blue-600 rounded-xl blur-sm opacity-50 group-hover:opacity-75 transition-opacity"></div>
                  <div className="relative w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  {t('app.name')}
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className={`hidden md:flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <LanguageToggle />
              
              {user && (
                <>
                  <Link 
                    to="/dashboard" 
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 rounded-lg hover:bg-gray-100/50 transition-all"
                  >
                    {t('navigation.dashboard')}
                  </Link>
                  
                  {/* User Profile Dropdown */}
                  <div className="relative" ref={profileMenuRef}>
                    <button
                      onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                      className={`flex items-center gap-2 px-2 py-2 hover:bg-gray-100/50 rounded-lg transition-all ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                      <div className="w-9 h-9 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center text-white text-sm font-semibold ring-2 ring-gray-200">
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {isProfileMenuOpen && (
                      <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-3 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 overflow-hidden`}>
                        <div className="px-4 py-4 bg-gradient-to-br from-gray-50 to-white">
                          <p className="text-sm font-semibold text-gray-900">{user.username}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{user.email || 'User Account'}</p>
                        </div>
                        <div className="py-2">
                          <Link
                            to="/dashboard"
                            className={`flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <Settings className="w-4 h-4" />
                            <span>Profile Settings</span>
                          </Link>
                          <button
                            onClick={handleLogout}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                          >
                            <LogOut className="w-4 h-4" />
                            <span>{t('navigation.logout')}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
                <LanguageToggle className="w-full justify-center mb-2" />
                {user && (
                  <>
                    <div className="px-4 py-4 bg-gray-50 rounded-xl mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center text-white font-semibold">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{user.username}</p>
                          <p className="text-xs text-gray-500">{user.email || 'User Account'}</p>
                        </div>
                      </div>
                    </div>
                    <Link 
                      to="/dashboard" 
                      className="px-4 py-3 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('navigation.dashboard')}
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="px-4 py-3 text-center text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      {t('navigation.logout')}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-20"></div>

      <div className="py-16 md:py-24 px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <Link
              to={`/chat/${slug}`}
              className="flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold px-7 py-4 rounded-xl transition-all shadow-lg"
            >
              <Eye className="w-5 h-5" />
              <span>Preview ChatBot</span>
            </Link>
            <Link
              to={`/${slug}`}
              target="_blank"
              className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-900 font-semibold px-7 py-4 rounded-xl border border-gray-200 transition-all"
            >
              <Globe className="w-5 h-5" />
              <span>View Public Page</span>
            </Link>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Basic Information</h2>
                <p className="text-gray-600 text-lg">Update the essential details for your chatbot.</p>
              </div>
              
              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    ChatBot Name *
                  </label>
                  <input
                    type="text"
                    className="w-full px-6 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter your chatbot name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Description
                  </label>
                  <textarea
                    className="w-full px-6 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Describe what your chatbot does"
                    rows={4}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Language
                  </label>
                  <select
                    className="w-full px-6 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    value={formData.language}
                    onChange={(e) => handleInputChange('language', e.target.value)}
                  >
                    <option value="en">English</option>
                    <option value="fa">Persian (فارسی)</option>
                  </select>
                </div>
              </div>

              <div className="border-t border-gray-200/50 mt-10 pt-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Template Selection</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                        formData.template_type === template.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                      onClick={() => handleTemplateSelect(template.id)}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 bg-gradient-to-br ${template.color} rounded-2xl flex items-center justify-center text-white text-3xl shadow-lg`}>
                          {template.icon}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900 mb-3 text-lg">{template.name}</h3>
                          <p className="text-gray-600 text-sm leading-relaxed">{template.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-200/50 mt-10 pt-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Knowledge Base</h3>
                
                <div className="space-y-8">
                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Upload Files (PDF, DOCX, TXT)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.docx,.txt"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="file-upload"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 text-lg mb-2">Click to upload files or drag and drop</p>
                        <p className="text-sm text-gray-500">Maximum file size: 10MB</p>
                      </label>
                    </div>
                  </div>

                  {/* Uploaded Files */}
                  {uploadedFiles.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Uploaded Files:</h3>
                      <div className="space-y-3">
                        {uploadedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-gray-400" />
                              <span className="text-sm text-gray-700 font-medium">{file.name}</span>
                              <span className="text-xs text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-all"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Knowledge Base Content */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Knowledge Base Content *
                    </label>
                    <textarea
                      className="w-full px-6 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                      value={knowledgeBase}
                      onChange={(e) => setKnowledgeBase(e.target.value)}
                      placeholder="Enter or paste your knowledge base content here. This is what your chatbot will use to answer questions."
                      rows={12}
                      required
                    />
                    <p className="text-sm text-gray-500 mt-3">
                      This content will be combined with any uploaded files to create your chatbot's knowledge base.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200/50 mt-10 pt-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Customization</h3>
                
                <div className="space-y-8">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Custom Greeting Message
                    </label>
                    <textarea
                      className="w-full px-6 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                      value={formData.custom_greeting}
                      onChange={(e) => handleInputChange('custom_greeting', e.target.value)}
                      placeholder="Enter a custom greeting message for your chatbot"
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Quick Questions
                    </label>
                    <div className="space-y-4">
                      {formData.quick_questions.map((question, index) => (
                        <div key={index} className="flex items-center gap-4">
                          <input
                            type="text"
                            className="flex-1 px-6 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            value={question}
                            onChange={(e) => {
                              const newQuestions = [...formData.quick_questions];
                              newQuestions[index] = e.target.value;
                              handleQuickQuestionsChange(newQuestions);
                            }}
                            placeholder="Enter a quick question"
                          />
                          <button
                            type="button"
                            onClick={() => removeQuickQuestion(index)}
                            className="p-3 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addQuickQuestion}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium px-4 py-2 rounded-lg hover:bg-blue-50 transition-all"
                      >
                        <Plus className="w-5 h-5" />
                        <span>Add Quick Question</span>
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <input
                      type="checkbox"
                      id="collect_user_info"
                      checked={formData.collect_user_info}
                      onChange={(e) => handleInputChange('collect_user_info', e.target.checked)}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="collect_user_info" className="text-sm font-medium text-gray-700">
                      Collect user information (name, email)
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-10">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold px-8 py-3 rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-6 h-6" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BotEditor; 